import React from 'react';
import { Autocomplete } from '@material-ui/lab';
import { TextField } from '@material-ui/core';
import { executeBackendQuery } from '../../requests/executeBackendQuery';
import { VENDOR_LIST } from '../../consts/queries';
import filter from '../../filter';
import { getOpenInCrmHref } from '../OpenInCrmButton/OpenInCrmButton';
import './SelectEscrowOfficer.scss';

const emptyQuickAdd = { FIRST_NAME: '', LAST_NAME: '', PHONE: '', EMAIL_ID: '', COMPANY_NAME: '' };
const QUICK_ADD_OPTION = { __quickAdd: true };

function normalizeName(value) {
    return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

export function getVendorDisplayName(v) {
    if (v.NAME && String(v.NAME).trim()) return String(v.NAME).trim();
    return [v.FIRST_NAME, v.LAST_NAME].filter(Boolean).join(' ').trim();
}

/**
 * The first vendor whose shown name is `name`, ignoring case and extra spaces.
 * With `isPreferred`, several such vendors give the first it accepts, or none.
 */
export function findVendorByName(vendors, name, isPreferred) {
    const wanted = normalizeName(name);
    if (!wanted) return null;
    const namesakes = (vendors || []).filter(v => normalizeName(getVendorDisplayName(v)) === wanted);
    if (isPreferred && namesakes.length > 1) return namesakes.find(isPreferred) || null;
    return namesakes[0] || null;
}

/** A-Z by the exact text the dropdown shows, so the list reads as alphabetized. */
export function sortVendorsAlphabetically(list) {
    const opts = { sensitivity: 'base', numeric: true };
    return [...list].sort((a, b) => getVendorDisplayName(a).localeCompare(getVendorDisplayName(b), undefined, opts));
}

/**
 * Company shown next to a contact's name. The FIRM link is the maintained
 * record and wins: VENDORS.COMPANY_NAME is a free-text snapshot that goes
 * stale when a company renames or is acquired (Landon Gann's COMPANY_NAME
 * still reads "Independent Financial" while his firm is "SouthState Bank"),
 * which made the pickers show — and fill in — the wrong lender. COMPANY_NAME
 * remains the fallback for vendors with no firm on file. (Claritask 547922)
 */
export function getVendorCompanyName(v) {
    if (!v) return '';
    const firm = String(v.FIRM_NAME || '').trim();
    if (firm) return firm;
    return String(v.COMPANY_NAME || '').trim();
}

function getEscrowOfficerHref(leadId, name) {
    if (leadId) return getOpenInCrmHref(leadId, 'account');
    const search = String(name || '').trim();
    return search ? `https://accounts.kore.co/crm/account?search=${encodeURIComponent(search)}` : null;
}

const DEFAULT_VENDOR_TYPE = 'Title Company';

/**
 * Vendor-contact picker: a searchable list of VENDORS rows of one VENDOR_TYPE,
 * plus a Quick add that creates the vendor in Kore and locally in one call.
 *
 * Used twice with different vendor types — escrow officers (Title Company) on
 * summit escrow tracking, and lender contacts (Lender) on the property Loan
 * Quotes tab (Claritask 548290). The class name is kept for the existing
 * styling and E2E selectors; `vendorType` is what makes it generic.
 */
export default class SelectEscrowOfficer extends React.Component {
    get vendorType() {
        return this.props.vendorType || DEFAULT_VENDOR_TYPE;
    }

    /** Noun used in the placeholder / edit affordance, e.g. "officer", "contact". */
    get contactNoun() {
        return this.props.contactNoun || 'officer';
    }

    constructor(props) {
        super(props);
        this.state = {
            vendors: [],
            inputValue: props.value || '',
            showQuickAdd: false,
            quickAddData: { ...emptyQuickAdd },
            quickAddBusy: false,
            quickAddError: null,
            isEditing: false,
            showCompanyDropdown: false,
        };
        this.handleQuickAddSave = this.handleQuickAddSave.bind(this);
    }

    componentDidMount() {
        const options = filter({ VENDOR_TYPE: this.vendorType }, 'vendor');
        // Match vendors page filter; high limit so officers are not missing due to SQL slice
        options.param.limit = 5000;
        options.param.offset = 0;
        executeBackendQuery({ queryName: VENDOR_LIST, options, expireTime: 300 }).then(res => {
            const vendors = sortVendorsAlphabetically((res && res.data || []).filter(v => v.DELETED !== '1'));
            this.setState({ vendors });
        });
    }

    componentDidUpdate(prevProps) {
        if (prevProps.value !== this.props.value) {
            this.setState({ inputValue: this.props.value || '' });
        }
    }

    handleQuickAddSave() {
        const quickAddData = { ...this.state.quickAddData };
        if (!quickAddData.FIRST_NAME || !quickAddData.LAST_NAME) return;

        this.setState({ quickAddBusy: true, quickAddError: null });

        const companyNameResolved = String(quickAddData.COMPANY_NAME || this.props.companyFilter || '').trim();

        fetch('/api/crm/create-lead', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                type: 'vendor',
                vendorType: this.vendorType,
                firstName: quickAddData.FIRST_NAME,
                lastName: quickAddData.LAST_NAME,
                phone: quickAddData.PHONE || '',
                email: quickAddData.EMAIL_ID || '',
                companyName: companyNameResolved,
            }),
        }).then(async res => {
            const json = await res.json().catch(() => null);
            if (!res.ok || !json || !json.success) {
                const error = json && (json.message || json.error) ? (json.message || json.error) : 'Failed to save. Please try again.';
                throw new Error(error);
            }

            const name = [quickAddData.FIRST_NAME, quickAddData.LAST_NAME].filter(Boolean).join(' ');
            const companyName = companyNameResolved;
            const leadId = json.leadId || null;
            const newVendor = {
                ARCIMEDES_ROW_ID: json.arcRowId,
                VENDOR_TYPE: this.vendorType,
                FIRST_NAME: quickAddData.FIRST_NAME,
                LAST_NAME: quickAddData.LAST_NAME,
                NAME: name,
                PHONE: quickAddData.PHONE || '',
                EMAIL_ID: quickAddData.EMAIL_ID || '',
                COMPANY_NAME: companyName,
                FIRM_ID: json.firmId || null,
                LEAD_ID: leadId,
            };

            this.setState(prev => ({
                vendors: sortVendorsAlphabetically([...prev.vendors, newVendor]),
                inputValue: name,
                showQuickAdd: false,
                quickAddData: { ...emptyQuickAdd },
                quickAddBusy: false,
                isEditing: false,
            }));
            this.props.onSelect(name, companyName, leadId, newVendor);
        }).catch(err => {
            this.setState({
                quickAddBusy: false,
                quickAddError: err && err.message ? err.message : 'Failed to save. Please try again.',
            });
        });
    }

    render() {
        const { value, disabled, companyFilter, onSelect, storedLeadId, disablePortal = false, plainField = false } = this.props;
        const { vendors, inputValue, showQuickAdd, quickAddData, quickAddBusy, quickAddError, isEditing, showCompanyDropdown } = this.state;

        const allCompanyNames = [...new Set(vendors.map(getVendorCompanyName).filter(Boolean))].sort();
        const companyInput = quickAddData.COMPANY_NAME || '';
        const filteredCompanies = companyInput
            ? allCompanyNames.filter(c => c.toLowerCase().includes(companyInput.toLowerCase()))
            : allCompanyNames;
        const isNewFirm = companyInput.trim() && !allCompanyNames.includes(companyInput.trim());

        const normalizedValue = normalizeName(value);
        const matchedVendor = findVendorByName(vendors, value);
        const leadId = storedLeadId || (matchedVendor ? matchedVendor.LEAD_ID : null);
        const href = getEscrowOfficerHref(leadId, value);

        if (disabled) {
            if (href && value) {
                return (
                    <div className="SelectEscrowOfficer-rowWrap">
                        <a
                            className="SelectEscrowOfficer-crmNameLink"
                            href={href}
                            target="crm-vendor"
                        >
                            <span className="SelectEscrowOfficer-crmNameLink_text">{value}</span>
                        </a>
                    </div>
                );
            }
            return <div>{value || ''}</div>;
        }

        // Editable + value selected: show the name as a blue hyperlink (opens CRM).
        // A small pencil button switches into edit mode to change the selection.
        if (value && !isEditing && !showQuickAdd && !plainField) {
            return (
                <div className="SelectEscrowOfficer-rowWrap">
                    <div className="SelectEscrowOfficer-row">
                        {href ? (
                            <a
                                className="SelectEscrowOfficer-crmNameLink"
                                href={href}
                                target="crm-vendor"
                            >
                                <span className="SelectEscrowOfficer-crmNameLink_text">{value}</span>
                            </a>
                        ) : (
                            <span className="SelectEscrowOfficer-crmNameLink_text">{value}</span>
                        )}
                        <button
                            type="button"
                            className="SelectEscrowOfficer-editBtn"
                            aria-label={`Change ${this.contactNoun}`}
                            title={`Change ${this.contactNoun}`}
                            onClick={() => this.setState({ isEditing: true, inputValue: value || '' })}
                        >
                            ✎
                        </button>
                    </div>
                </div>
            );
        }

        // Contacts already used elsewhere on the same list (Loan Bids) are not
        // offered again; the saved value of this row is always kept.
        const excluded = new Set((this.props.excludeIds || []).map(String));
        const filtered = sortVendorsAlphabetically(vendors).filter((v) => !excluded.has(String(v.ARCIMEDES_ROW_ID)));

        const selectedFromAll = findVendorByName(vendors, value);
        const selectedInPool = findVendorByName(filtered, value);
        // Once the user edits the text away from the saved name, clear selection so MUI
        // shows the full filtered list instead of collapsing to Quick add only.
        const inputMatchesSaved = normalizeName(inputValue) === normalizedValue;
        const autocompleteValue =
            selectedFromAll && inputMatchesSaved ? selectedInPool || selectedFromAll : null;

        return (
            <div className={plainField ? 'SelectEscrowOfficer SelectEscrowOfficer--plain' : 'SelectEscrowOfficer'}>
                <div className="SelectEscrowOfficer-rowWrap">
                    <div className="SelectEscrowOfficer-row">
                        <div className="SelectEscrowOfficer-field">
                <Autocomplete
                    className="SelectEscrowOfficer-autocomplete"
                    classes={{
                        // v4 Autocomplete has no PopperProps; the popper's class goes here.
                        // Its z-index (above dialogs) is in SelectEscrowOfficer.scss.
                        popper: 'SelectEscrowOfficer-popper',
                        paper: 'SelectEscrowOfficer-autocompletePaper',
                        listbox: 'SelectEscrowOfficer-autocompleteListbox',
                        option: 'SelectEscrowOfficer-autocompleteOption',
                    }}
                    options={filtered}
                    getOptionLabel={(option) => {
                        if (option && option.__quickAdd) return '';
                        return option ? getVendorDisplayName(option) : '';
                    }}
                    renderOption={(option) => {
                        if (option && option.__quickAdd) {
                            return (
                                <div
                                    className="SelectEscrowOfficer-quickAddRow"
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const parts = inputValue.trim().split(' ');
                                        this.setState({
                                            showQuickAdd: true,
                                            quickAddData: {
                                                ...emptyQuickAdd,
                                                FIRST_NAME: parts[0] || '',
                                                LAST_NAME: parts.slice(1).join(' ') || '',
                                                COMPANY_NAME: companyFilter || '',
                                            },
                                        });
                                    }}
                                >
                                    + Quick add
                                </div>
                            );
                        }
                        return (
                            <div className="SelectEscrowOfficer-optionRow">
                                <span className="SelectEscrowOfficer-optionName">
                                    {getVendorDisplayName(option)}{getVendorCompanyName(option) ? ` - ${getVendorCompanyName(option)}` : ''}
                                </span>
                            </div>
                        );
                    }}
                    value={autocompleteValue}
                    inputValue={inputValue}
                    onInputChange={(e, newInput, reason) => {
                        if (reason === 'reset') return;
                        this.setState({ inputValue: newInput, showQuickAdd: false });
                    }}
                    onChange={(e, newValue) => {
                        if (!newValue || newValue.__quickAdd) return;
                        const name = getVendorDisplayName(newValue);
                        this.setState({ inputValue: name, isEditing: false });
                        onSelect(name, getVendorCompanyName(newValue), newValue.LEAD_ID || null, newValue);
                    }}
                    disableClearable
                    disablePortal={disablePortal}
                    forcePopupIcon={false}
                    filterOptions={(options, state) => {
                        const q = (state.inputValue || '').toLowerCase();
                        const results = q
                            ? options.filter(v => {
                                const haystack = `${getVendorDisplayName(v)} ${getVendorCompanyName(v)}`.toLowerCase();
                                return haystack.includes(q);
                            })
                            : options;
                        // Quick add always sits at the end of the list, not only
                        // when nothing matched — otherwise there is no visible way
                        // to add someone who isn't on the list yet. (Claritask 547922)
                        return [...results, QUICK_ADD_OPTION];
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: null,
                                disableUnderline: true,
                                style: { padding: plainField ? 0 : '0 4px', fontSize: 'inherit', fontFamily: 'inherit', fontWeight: 'inherit', color: 'inherit' },
                            }}
                            inputProps={{
                                ...params.inputProps,
                                style: { padding: 0, textAlign: plainField ? 'left' : 'center', fontSize: 'inherit', fontFamily: 'inherit', fontWeight: 'inherit' },
                            }}
                            variant="standard"
                            placeholder={`Search ${this.contactNoun}...`}
                        />
                    )}
                />
                        </div>
                    </div>
                </div>
                {showQuickAdd && (
                    <div style={{ marginTop: '8px', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#fff', fontSize: '12px', zIndex: 10, position: 'relative' }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '6px', fontSize: '13px' }}>Quick Add Vendor</div>
                        {quickAddError && <div style={{ color: 'red', marginBottom: '4px' }}>{quickAddError}</div>}
                        <input
                            placeholder="First Name *"
                            value={quickAddData.FIRST_NAME}
                            onChange={e => this.setState({ quickAddData: { ...quickAddData, FIRST_NAME: e.target.value } })}
                            style={{ width: '100%', marginBottom: '4px', padding: '4px 6px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '12px', boxSizing: 'border-box' }}
                        />
                        <input
                            placeholder="Last Name *"
                            value={quickAddData.LAST_NAME}
                            onChange={e => this.setState({ quickAddData: { ...quickAddData, LAST_NAME: e.target.value } })}
                            style={{ width: '100%', marginBottom: '4px', padding: '4px 6px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '12px', boxSizing: 'border-box' }}
                        />
                        <input
                            placeholder="Phone"
                            value={quickAddData.PHONE}
                            onChange={e => this.setState({ quickAddData: { ...quickAddData, PHONE: e.target.value } })}
                            style={{ width: '100%', marginBottom: '4px', padding: '4px 6px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '12px', boxSizing: 'border-box' }}
                        />
                        <input
                            placeholder="Email"
                            value={quickAddData.EMAIL_ID}
                            onChange={e => this.setState({ quickAddData: { ...quickAddData, EMAIL_ID: e.target.value } })}
                            style={{ width: '100%', marginBottom: '4px', padding: '4px 6px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '12px', boxSizing: 'border-box' }}
                        />
                        <div style={{ position: 'relative', marginBottom: '6px' }}>
                            <input
                                placeholder="Company Name"
                                value={quickAddData.COMPANY_NAME}
                                autoComplete="off"
                                onChange={e => this.setState({
                                    quickAddData: { ...quickAddData, COMPANY_NAME: e.target.value },
                                    showCompanyDropdown: true,
                                })}
                                onFocus={() => this.setState({ showCompanyDropdown: true })}
                                onBlur={() => setTimeout(() => this.setState({ showCompanyDropdown: false }), 150)}
                                style={{ width: '100%', padding: '4px 6px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '12px', boxSizing: 'border-box' }}
                            />
                            {showCompanyDropdown && (filteredCompanies.length > 0 || isNewFirm) && (
                                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, maxHeight: '180px', overflowY: 'auto', border: '1px solid #ccc', borderRadius: '3px', background: '#fff', zIndex: 200, boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }}>
                                    {filteredCompanies.map(name => (
                                        <div
                                            key={name}
                                            onMouseDown={e => {
                                                e.preventDefault();
                                                this.setState({ quickAddData: { ...quickAddData, COMPANY_NAME: name }, showCompanyDropdown: false });
                                            }}
                                            style={{ padding: '6px 8px', cursor: 'pointer', fontSize: '12px', borderBottom: '1px solid #f0f0f0' }}
                                            onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                                            onMouseLeave={e => e.currentTarget.style.background = ''}
                                        >
                                            {name}
                                        </div>
                                    ))}
                                    {isNewFirm && (
                                        <div
                                            onMouseDown={e => {
                                                e.preventDefault();
                                                this.setState({ showCompanyDropdown: false });
                                            }}
                                            style={{ padding: '6px 8px', cursor: 'pointer', fontSize: '12px', color: '#1976d2', fontWeight: 600 }}
                                            onMouseEnter={e => e.currentTarget.style.background = '#e8f0fe'}
                                            onMouseLeave={e => e.currentTarget.style.background = ''}
                                        >
                                            + Add firm "{companyInput.trim()}"
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: '4px' }}>
                            <button
                                onClick={this.handleQuickAddSave}
                                disabled={quickAddBusy || !quickAddData.FIRST_NAME || !quickAddData.LAST_NAME}
                                style={{ flex: 1, padding: '5px', backgroundColor: '#1976d2', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px', opacity: (quickAddBusy || !quickAddData.FIRST_NAME || !quickAddData.LAST_NAME) ? 0.6 : 1 }}
                            >
                                {quickAddBusy ? 'Saving...' : 'Add'}
                            </button>
                            <button
                                onClick={() => this.setState({ showQuickAdd: false, quickAddData: { ...emptyQuickAdd }, quickAddError: null })}
                                style={{ flex: 1, padding: '5px', backgroundColor: '#f5f5f5', border: '1px solid #ccc', borderRadius: '3px', cursor: 'pointer', fontSize: '12px' }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}
